import type { GloryShopLogDoc } from './glory-log.types';

/** 기본 버킷 타임존 = KST(+9h). 운영이 한국 기준으로 일자를 읽는다. */
const DEFAULT_TZ_OFFSET_MIN = 9 * 60;

export interface GloryAgg {
  kpi: { totalGlory: number; purchases: number; buyers: number; avgPerBuyer: number };
  daily: { date: string; glory: number; count: number }[];
  byItem: { itemId: string; count: number; glory: number; buyers: number; pulls: number }[];
  byAccount: { account: string; glory: number; count: number }[];
}

/** epoch ms → 오프셋 적용 YYYY-MM-DD. */
export function dayKeyOf(timestamp: number, tzOffsetMin: number): string {
  return new Date(timestamp + tzOffsetMin * 60_000).toISOString().slice(0, 10);
}

/** 환불 행은 자산이 되돌아갔으므로 소비로 치지 않는다. */
const isCounted = (log: GloryShopLogDoc): boolean => log.refunded !== true;

export function aggregateGloryLogs(
  logs: GloryShopLogDoc[],
  tzOffsetMin: number = DEFAULT_TZ_OFFSET_MIN
): GloryAgg {
  const rows = logs.filter(isCounted);

  const daily = new Map<string, { glory: number; count: number }>();
  const items = new Map<
    string,
    { count: number; glory: number; pulls: number; buyers: Set<string> }
  >();
  const accounts = new Map<string, { glory: number; count: number }>();
  let totalGlory = 0;

  for (const log of rows) {
    totalGlory += log.cost;

    const dayKey = dayKeyOf(log.timestamp, tzOffsetMin);
    const day = daily.get(dayKey) ?? { glory: 0, count: 0 };
    day.glory += log.cost;
    day.count += 1;
    daily.set(dayKey, day);

    const item = items.get(log.itemId) ?? {
      count: 0,
      glory: 0,
      pulls: 0,
      buyers: new Set<string>(),
    };
    item.count += 1;
    item.glory += log.cost;
    item.pulls += log.pullCount ?? 1;
    item.buyers.add(log.account);
    items.set(log.itemId, item);

    const acc = accounts.get(log.account) ?? { glory: 0, count: 0 };
    acc.glory += log.cost;
    acc.count += 1;
    accounts.set(log.account, acc);
  }

  const buyers = accounts.size;
  return {
    kpi: {
      totalGlory,
      purchases: rows.length,
      buyers,
      avgPerBuyer: buyers === 0 ? 0 : Math.round(totalGlory / buyers),
    },
    daily: [...daily.entries()]
      .map(([date, v]) => ({ date, glory: v.glory, count: v.count }))
      .sort((a, b) => a.date.localeCompare(b.date)),
    byItem: [...items.entries()]
      .map(([itemId, v]) => ({
        itemId,
        count: v.count,
        glory: v.glory,
        buyers: v.buyers.size,
        pulls: v.pulls,
      }))
      .sort((a, b) => b.glory - a.glory),
    byAccount: [...accounts.entries()]
      .map(([account, v]) => ({ account, glory: v.glory, count: v.count }))
      .sort((a, b) => b.glory - a.glory),
  };
}
